/**
 * NOTE (Derek, 2026-02-05): CSS value parsing utilities for computed styles
 *
 * These utilities convert CSS computed style values into the formats
 * expected by the modifier panel components.
 */

import tinycolor from "tinycolor2";

/**
 * Parse a CSS pixel value string to a number, optionally passing through
 * allowable non-unit values like "auto".
 *
 * @example
 * parsePixelValue("16px")                              // => 16
 * parsePixelValue("0px")                               // => undefined
 * parsePixelValue("auto")                              // => undefined
 * parsePixelValue("auto", { allowableNonUnitValues: ["auto"] }) // => "auto"
 */
export function parsePixelValue(value: string | undefined): number | undefined;
export function parsePixelValue<const Passthrough extends string>(
  value: string | undefined,
  opts: { allowableNonUnitValues: readonly Passthrough[] },
): number | Passthrough | undefined;
export function parsePixelValue(
  value: string | undefined,
  opts?: { allowableNonUnitValues: readonly string[] },
): number | string | undefined {
  if (!value || value === "0px") {
    return undefined;
  }

  if (opts?.allowableNonUnitValues.includes(value)) {
    return value;
  }

  const match = value.match(/^(-?\d+(?:\.\d+)?)/);
  if (!match?.[1]) {
    return undefined;
  }

  const num = Number.parseFloat(match[1]);
  return isNaN(num) ? undefined : num;
}

/**
 * Parse a CSS color value. Returns null for invalid, transparent, or fully-
 * transparent colors (alpha === 0). Returns a hex8 string otherwise so
 * downstream components always receive a consistent format.
 */
export function parseColorValue(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  const color = tinycolor(value);
  if (!color.isValid() || color.getAlpha() === 0) {
    return null;
  }

  return color.toHex8String();
}
